import {
    Component,
    OnInit,
    OnDestroy,
    Inject,
    ViewChild,
    ViewChildren,
    QueryList,
    ElementRef,
    HostListener,
    Injector,
    Input,
    ChangeDetectorRef
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { APP_BASE_HREF } from '@angular/common';
import { BehaviorSubject, Subscription, combineLatest, of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { FilterService } from '../../../Filter/services/abstract-filter.service';
import { SidebarColorThemesEnum } from '../../../Forms/enums/sidebar-color-themes.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { LanguageTagEnum } from '../../../../Core/Localization/enums/language-tag.enum';
import { LeftSidebarListItem, LeftSidebarSubitem } from '../../models/left-sidebar-list-item.model';
import { LeftSidebarLocalizations } from '../../models/left-sidebar-localizations.model';
import { LeftSidebarService } from '../../services/left-sidebar.service';
import { KebabMenuComponent } from '../../../Forms/components/KebabMenu/kebab-menu.component';
import { focusManagementHelper } from '../../../Misc/helpers/focus/focus-management.helper';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { ActionsEnum } from '../../../Basic/enums/actions.enum';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ManageLhpListDialogComponent } from './ManageLhpListDialog/manage-lhp-list-dialog.component';
import { DisplayGroupModel } from '../../models/display-group.model';
import { DisplayGroupTypeEnum } from '../../../../api/displayGroups/enums/display-group-type.enum';
import { QuotaUsageIndicatorEnum } from '../../../../Monitor/Shared/enums/quota-usage-indicator.enum';

type SelectListItemEvent = KeyboardEvent | PointerEvent;
enum LHPItemType {
    group,
    item
}
interface LHPItem {
    id: string;
    type: LHPItemType;
    item?: any;
    group?: DisplayGroupModel;
    collapsed: boolean;
}

@Component({
    selector: 'agpr-left-sidebar',
    templateUrl: './left-sidebar.component.html',
    styleUrls: ['./left-sidebar.component.scss'],
    providers: [
        {
            provide: FilterService,
            useExisting: LeftSidebarService
        },
        {
            provide: 'SortOrderServiceInterface',
            useExisting: LeftSidebarService
        }
    ],
    standalone: false
})
export class LeftSidebarComponent implements OnInit, OnDestroy {

    private subscriptions: Subscription[] = [];
    public dropdownItems: DropdownOptionModel[] = [];
    public selectedDropdownItem: DropdownOptionModel = null;
    public listItems: LeftSidebarListItem[] = [];
    public groupedListItems: DisplayGroupModel[] = [];
    public ungroupedListItems: any[] = [];
    public showGroupHeaders: boolean = false;
    public hiddenListItems: LeftSidebarListItem[] = [];
    public loadingItems: boolean = false;
    public selectedItem: LeftSidebarListItem | null = null;
    public listIsComplete: boolean = false;
    public listIsReorderable: boolean = false;
    public buttonEnabled: boolean = false;
    public localizations: LeftSidebarLocalizations;
    public hasFilters: boolean = false;
    public hasFilterChips: boolean = false;
    public hasBrandGUID: boolean;
    public quickFilterPlaceholder: string;
    public quickFilterMinLength = 3;
    public hideSidebar = false;
    public hideAmplifyButton = true;
    public hideFilter = true;
    public hideList: boolean = false;
    public french: boolean = false;
    public isKeyboardSelection: boolean = false;
    public selectedListItemIndex: number = -1;
    public primaryHeading: string;
    public collapseSidebar = false;
    public collapseList = false;
    public showListPanel = false;
    public dashboardListItem: LeftSidebarListItem | null = null;
    public avgCharLength = 9;
    public showUngroupedViewButton: boolean = false;
    public showUngroupedView: boolean = false;
    selectedSubitem: LeftSidebarSubitem;
    selectedViewMode: string;
    showSubitems: boolean = false;
    secondaryOptionLabel: string;
    subitems: LeftSidebarSubitem[] = [];

    readonly sidebarColorThemes = SidebarColorThemesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly actionsEnum = ActionsEnum;
    readonly ITEM_HEIGHT = 40;
    readonly SUB_ITEM_HEIGHT = 30;
    readonly listClassName = 'agpr-leftSidebarItems-list';
    readonly itemLabelClassName = 'agpr-leftSidebarItems-itemLabel';
    readonly displayGroupTypes = DisplayGroupTypeEnum;
    readonly lhpItemTypes = LHPItemType;
    readonly quotaUsageIndicatorEnum = QuotaUsageIndicatorEnum;

    @Input() hideManageListDialog: boolean = false;
    @Input() id: string = 'leftSideBar';

    @ViewChild('virtualScrollPanel') virtualScrollPanel: CdkVirtualScrollViewport;
    @ViewChild('listItemList') listItemList: ElementRef;
    @ViewChildren('kebabMenu') kebabMenus: QueryList<KebabMenuComponent>;

    agilityLogoUrl: string;

    flattenedItems: any[];

    isInitialLoad: boolean = true;

    private manageListMenuOption = {
        label: null,
        command: () => this.openManageListTakeover()
    };

    public manageListMenuOptions: MenuItem[] = [
        this.manageListMenuOption,
        {
            label: 'Common.lhp.manageList.create',
            command: () => this.sidebarService.createNewGroup()
        }
    ];

    public groupOptions: MenuItem[] = [
        {
            label: 'Common.lhp.groups.options.rename',
            command: (event) => this.sidebarService.groupActions((event as any).data, ActionsEnum.rename)
        },
        {
            label: 'Common.lhp.groups.options.delete',
            command: (event) => this.sidebarService.groupActions((event as any).data, ActionsEnum.delete)
        }
    ];

    actionButtons = [];

    constructor(
        @Inject(LeftSidebarService) public sidebarService: LeftSidebarService,
        public localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService,
        public configService: ConfigService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public coreDialogService: CoreDialogService,
        public injector: Injector,
        private cd: ChangeDetectorRef
    ) {
        this.hasBrandGUID = configService.hasBrandGUID();
        if (this.hasBrandGUID) {
            this.agilityLogoUrl = this.appBaseHref + 'assets/images/powered-by-agility-dark.svg';
        }
    }

    @HostListener('window:keyup.escape', ['$event']) onKeyDown(e: KeyboardEvent) {
        this.hideSubitems();
    }

    ngOnInit() {
        this.subscriptions.push(
            this.sidebarService.getDropdownItems().subscribe(
                (items) => {
                    this.dropdownItems = items;
                }
            ),
            this.localizationService.getLocalizationMapChange().subscribe(
                () => {
                    this.setDropDownItemsLocalization();
                }
            ),
            this.sidebarService.getSelectedDropdownItem().subscribe(
                (item) => {
                    this.selectedDropdownItem = item;
                }
            ),
            this.sidebarService.getListItems().subscribe(
                (items) => {
                    if (items === null) {
                        this.loadingItems = true;
                    } else {
                        this.listItems = items;
                    }
                }
            ),
            this.sidebarService.getListIsComplete().subscribe(
                (isComplete) => {
                    this.listIsComplete = isComplete;
                }
            ),
            this.sidebarService.getSelectedListItem().subscribe(
                (item) => {
                    if (item && this.listItems?.length > 0) {
                        this.selectedListItemIndex = this.listItems.findIndex(listItem => listItem.value === +item.value);
                    }
                    this.selectedItem = item;
                }
            ),
            this.sidebarService.newListItemId$.subscribe(id => {
                const index = this.flattenedItems?.findIndex(itm => itm?.item?.value === id);
                if (index > 0) {
                    this.virtualScrollPanel.scrollToIndex(index - 1);
                }
            }),
            this.sidebarService.getReorderEnabled().subscribe(
                (isReorderable) => {
                    this.listIsReorderable = isReorderable;
                }
            ),
            // TODO no need to listen to current account when canvas is released
            combineLatest([
                this.sidebarService.getLocalizations(),
                this.currentUserStateService.getAccount()]
            ).subscribe(
                ([localizations, account]) => {
                    this.localizations = localizations;
                    this.manageListMenuOption.label =
                        this.localizationService.transform('Common.lhp.manageList.manage', {listItems: localizations.itemsHeading});
                    if (this.localizations.secondaryAmplifyButton) {
                        this.actionButtons = [
                            {
                                label: this.localizationService.transform(this.localizations.amplifyButton),
                                command: () => this.sidebarService.amplifyButtonClicked()
                            },
                            {
                                label: this.localizationService.transform(this.localizations.secondaryAmplifyButton),
                                command: () => this.sidebarService.secondaryAmplifyButtonClicked()
                            }
                        ];
                    } else {
                        this.actionButtons = [];
                    }
                }
            ),
            this.sidebarService.isAmplifyButtonEnabled().subscribe(
                (enabled) => {
                    this.buttonEnabled = enabled;
                }
            ),
            this.sidebarService.getFilters().subscribe(
                (filters) => {
                    this.hasFilters = filters && filters.length > 0;
                    this.hasFilterChips = this.hasFilters && !!filters.find(f => f.chips?.length > 0);
                }
            ),
            this.sidebarService.getQuickFilterPlaceholder().subscribe(
                (placeholder) => {
                    this.quickFilterPlaceholder = placeholder;
                }
            ),
            this.sidebarService.getHideSidebar().subscribe(hide => this.hideSidebar = hide),
            this.sidebarService.getHideAmplifyButton().subscribe(hide => this.hideAmplifyButton = hide),
            this.sidebarService.getHideFilter().subscribe(hide => this.hideFilter = hide),
            this.sidebarService.getHideList().subscribe(hide => this.hideList = hide),
            this.sidebarService.getHiddenListItems()
                .pipe(filter(x => !!x))
                .subscribe((hiddenItems) => {
                    this.hiddenListItems = hiddenItems;
                }),
            this.currentUserStateService.getUILanguageTag().subscribe(tag => {
                if (!!tag) {
                    switch (tag) {
                        case LanguageTagEnum.fr_CA:
                        case LanguageTagEnum.fr_FR:
                            this.french = true;
                            break;
                        case LanguageTagEnum.en_US:
                        default:
                            this.french = false;
                            break;
                    }
                }
            }),
            this.sidebarService.getPrimaryHeading().subscribe(heading => {
                this.primaryHeading = heading;
            }),
            this.sidebarService.subitems$.subscribe(x => {
                this.subitems = x;
                this.alignPanel();
            }),
            this.sidebarService.getGroupedListItems().subscribe(groups => {
                this.groupedListItems = groups;
                this.ungroupedListItems = [];
                if (groups.length) {
                    const hasFavorites = this.groupedListItems.find(group => group.type === DisplayGroupTypeEnum.favorites)?.listItems?.length > 0;
                    this.showGroupHeaders = this.groupedListItems.length > 2 || hasFavorites;
                    this.showUngroupedViewButton = !this.groupedListItems.every(item => {
                        return item.type === DisplayGroupTypeEnum.uncategorized
                            || item.type === DisplayGroupTypeEnum.favorites && item.listItems.length === 0;
                    });

                    // Skip the synthetic "favorites" group: its items are cross-listed copies of items that
                    // already appear under their real group (or uncategorized), so including it here would
                    // duplicate those items in the flat/ungrouped view.
                    this.groupedListItems
                        .filter(group => group.type !== DisplayGroupTypeEnum.favorites)
                        .forEach(group => {
                            const id = !!group.id ? group.id : group.type;
                            this.ungroupedListItems.push(
                                ...group.listItems.map((item, index) => {
                                    return {
                                        id: `${id}_${index}`,
                                        type: LHPItemType.item,
                                        item: item
                                    };
                                })
                            );
                        });

                    this.updateFlattenedItems();

                    if (this.isInitialLoad) {
                        this.isInitialLoad = false;
                        setTimeout(() => {
                            this.scrollToSelectedItem();
                            this.loadingItems = false;
                        }, 30);
                    }
                } else {
                    this.loadingItems = false;
                }
            }),
            this.sidebarService.getTopListItem().subscribe(item => this.dashboardListItem = item),
            this.sidebarService.modeChangeComplete$.pipe(
            ).subscribe((_) => {
                // Reset scroll panel to top after mode change
                this.virtualScrollPanel?.scrollToOffset(0);
            })
        );
        this.quickFilterMinLength = this.sidebarService.getQuickFilterMinLength();
    }

    ngOnDestroy() {
        this.subscriptions.forEach((value) => {
            value.unsubscribe();
        });
    }

    public toggleUngroupedView(): void {
        this.showUngroupedView = !this.showUngroupedView;
        this.updateFlattenedItems();
    }

    updateFlattenedItems(): void {
        if (this.showUngroupedView) {
            this.flattenedItems = this.ungroupedListItems;
        } else {
            this.flattenGroups();
        }
    }

    flattenGroups() {
        const existingOffset = this.virtualScrollPanel?.measureScrollOffset() || 0;
        this.flattenedItems = [];
        this.groupedListItems.forEach(group => {
            const id = !!group.id ? group.id : group.type;
            this.flattenedItems.push({
                id: id,
                type: LHPItemType.group,
                group: group,
                collapsed: group.collapsed
            });
            if (!group.collapsed) {
                this.flattenedItems.push(
                    ...group.listItems.map((item, index) => {
                        return {
                            id: `${id}_${index}`,
                            type: LHPItemType.item,
                            item: item
                        };
                    })
                );
            }
        });

        if (!this.isInitialLoad) {
            requestAnimationFrame(() => {
                this.virtualScrollPanel?.scrollToOffset(existingOffset);
            });
        }
    }

    toggleGroup(toggledGroup: LHPItem) {
        const group = this.groupedListItems.find(x => x.id === +toggledGroup.id || x.type === toggledGroup.id);
        if (group) {
            group.collapsed = !group.collapsed;
        }
        this.flattenGroups();
    }

    showAllHiddenItems() {
        this.sidebarService.setVisibilityFilter();
    }

    public dropdownSelectionChanged(item: DropdownOptionModel): void {
        requestAnimationFrame(() => {
            this.sidebarService.selectDropdownItem(item);
        });
    }

    private setDropDownItemsLocalization() {
        this.dropdownItems.forEach(
            dropdownOptionModel => {
                const newLabel = this.localizationService.transform(dropdownOptionModel.label);
                if (newLabel !== '') {
                    dropdownOptionModel.label = newLabel;
                }
            }
        );
    }

    private restoreFocus(): void {
        requestAnimationFrame(() => {
            focusManagementHelper.restoreListFocus(this.listClassName, this.selectedListItemIndex, this.itemLabelClassName);
            this.isKeyboardSelection = false;
        });
    }

    selectListItem(item, event?: SelectListItemEvent): void {
        if (item.disabled) {
            return;
        }

        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        if (event instanceof KeyboardEvent || (event instanceof PointerEvent && event.offsetX === 0)) {
            this.isKeyboardSelection = true;
        }
        this.selectedSubitem = null;
        this.sidebarService.selectListItem(item);
    }

    hideKebabMenu(event){
        const hoverKebabId = event?.target?.querySelector('agpr-kebab-menu')?.getAttribute('data-id');
        this.kebabMenus.forEach(element => {
            if (hoverKebabId !== element.id) {
                element.hide();
            }
        });
    }

    collapseLeftSidebar(): void {
        this.collapseSidebar = !this.collapseSidebar;
    }

    loadSubitems(listItem: LeftSidebarListItem ): void {
        this.secondaryOptionLabel = listItem.secondaryOption;
        this.showSubitems = true;
        this.sidebarService.loadSubitems(listItem.value);
    }

    alignPanel(): void {
        const panelElement = document.getElementById('leftSidebar_subListItems');
        if (panelElement) {
            const maxLabelLength = this.subitems.reduce((max, item) => {
                return item.label.length > max ? item.label.length : max;
            }, 0);
            let calculatedWidth = maxLabelLength * this.avgCharLength;
            calculatedWidth = Math.max(220, calculatedWidth);
            panelElement.style.width = calculatedWidth + 'px';
            panelElement.style.right = `${-calculatedWidth}px`;
            panelElement.style.visibility = 'visible';
        }
    }

    hideSubitems(): void {
        this.showSubitems = false;
    }

    selectSubitem(subitem: LeftSidebarSubitem, event?: SelectListItemEvent): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }

        if (event instanceof KeyboardEvent || (event instanceof PointerEvent && event.offsetX === 0)) {
            this.isKeyboardSelection = true;
        }

        this.selectedSubitem = subitem;
        this.hideSubitems();
        this.selectedItem = this.listItems.find(item => item.value === this.selectedSubitem.resourceId);
        this.sidebarService.selectSubitem(subitem);
    }

    openManageListTakeover() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const overlayContainer = document.getElementById('main-content');
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer,
                header: this.localizationService.transform('Common.lhp.manageList.dialog.heading', {listItem: this.localizations.itemsHeading}),
                noContentMessage: this.localizations.noItems
            }
        };
        this.coreDialogService.open(ManageLhpListDialogComponent, io, this.injector);
    }

    trackByFn(index: number, item: any): number {
        return item.id;
    }

    scrollToSelectedItem(): void {
        if (!this.selectedItem || !this.flattenedItems?.length || !this.virtualScrollPanel) {
            return;
        }

        const index = this.flattenedItems.findIndex(
            itm => itm.type === LHPItemType.item && +itm?.item?.value === +this.selectedItem.value
        );

        if (index >= 0) {
            const viewHeight = this.virtualScrollPanel.getViewportSize();
            const itemsInViewport = Math.floor(viewHeight / this.ITEM_HEIGHT);
            const scrollIndex = Math.max(0, index - Math.floor(itemsInViewport / 2));
            const offset = scrollIndex * this.ITEM_HEIGHT;
            this.virtualScrollPanel.scrollToOffset(offset, 'smooth');
        }
    }
}
